import React, { useState, useMemo } from 'react';
import { Typography, Modal } from 'antd';
import { CloudOutlined, DatabaseOutlined } from '@ant-design/icons';
import { Box } from 'lucide-react';
import './ClusterDiagram.css';

const { Text } = Typography;

/* ────────────── helpers ────────────── */
const groupPodsByLabel = podsObj => {
  const grouped = {};
  Object.values(podsObj).forEach(pod => {
    const labelKey = pod.labels?.app
      ? 'app'
      : pod.labels?.k8s_app
      ? 'k8s_app'
      : null;
    const groupName = labelKey ? pod.labels[labelKey] : 'Other';
    if (!grouped[groupName]) grouped[groupName] = [];
    grouped[groupName].push(pod.name);
  });
  return grouped;
};

/* ────────────── component ────────────── */
const ClusterDiagram = ({ data }) => {
  /* state */
  const [selectedNode, setSelectedNode] = useState(null);
  const [modalInfo, setModalInfo] = useState({
    visible: false,
    groupName: '',
    pods: [],
  });

  /* flatten nodes from all top-level clusters */
  const nodesObj = useMemo(() => {
    if (!data) return {};
    const all = {};
    Object.values(data).forEach(cluster =>
      Object.entries(cluster.nodes || {}).forEach(([id, node]) => {
        all[id] = node; // if duplicate IDs across clusters are impossible, this is fine
      })
    );
    return all;
  }, [data]);

  /* pre-compute “pods grouped by label” per node */
  const nodeToGroupedPods = useMemo(() => {
    const map = {};
    Object.entries(nodesObj).forEach(([nodeId, node]) => {
      map[nodeId] = groupPodsByLabel(node.pods || {});
    });
    return map;
  }, [nodesObj]);

  /* still waiting for data? */
  if (!data) return <div>Loading diagram…</div>;

  /* click handlers */
  const handleNodeClick = nodeId =>
    setSelectedNode(prev => (prev === nodeId ? null : nodeId));

  const handleGroupClick = (groupName, pods) =>
    setModalInfo({ visible: true, groupName, pods });

  const handleCloseModal = () =>
    setModalInfo(prev => ({ ...prev, visible: false }));

  /* ────────────── render ────────────── */
  return (
    <div className="diagram-container">
      {/* CLOUD LEVEL */}
      <div className="tree-level cloud">
        <div className="circle-card">
          <CloudOutlined style={{ fontSize: 28, color: '#1890ff' }} />
          <Text>Cluster&nbsp;Manager</Text>
        </div>
      </div>

      <div className="tree-connect">
        <div className="arrow-down" />
      </div>

      {/* NODES LEVEL */}
      <div className="tree-level nodes">
        {Object.keys(nodesObj)
          .sort()
          .map(nodeId => (
            <div
              key={nodeId}
              className={`circle-card node-card ${
                selectedNode === nodeId ? 'node-selected' : ''
              }`}
              onClick={() => handleNodeClick(nodeId)}
            >
              <DatabaseOutlined style={{ fontSize: 24, color: '#faad14' }} />
              <Text>{nodeId}</Text>
            </div>
          ))}
      </div>

      {/* POD GROUPS LEVEL (only when a node is selected) */}
      {selectedNode && (
        <>
          <div className="tree-connect">
            <div className="arrow-down small" />
          </div>

          <div className="tree-level pods">
            {Object.entries(nodeToGroupedPods[selectedNode]).map(
              ([groupName, podList]) => (
                <div
                  key={`${selectedNode}-${groupName}`}
                  className="pod-column"
                >
                  <div className="tree-connect">
                    <div className="arrow-down small" />
                  </div>
                  <div
                    className="circle-card pod-group-card"
                    onClick={() => handleGroupClick(groupName, podList)}
                  >
                    <Box size={20} />
                    <Text>{groupName}</Text>
                  </div>
                </div>
              )
            )}
          </div>
        </>
      )}

      {/* POD LIST MODAL */}
      <Modal
        title={modalInfo.groupName}
        visible={modalInfo.visible}   /* Ant Design v4 prop */
        onCancel={handleCloseModal}
        footer={null}
      >
        <ul>
          {modalInfo.pods.map((pod, idx) => (
            <li key={idx}>{pod}</li>
          ))}
        </ul>
      </Modal>
    </div>
  );
};

export default ClusterDiagram;
